/**
 * What each identity colour MEANS — the one place a source or a field group is
 * given a slot.
 *
 * ## WHY A TABLE, AND WHY HERE
 *
 * `theme.css` declares four identity colours by slot (`ident-a` … `ident-d`),
 * the way it declares the chart series: a component that draws one never says
 * "sky" or "apricot", and the hexes can move without a component noticing.
 * The other half of that bargain is that *some* file has to say "Upload is
 * slot a". If every screen said it for itself, the first screen to disagree
 * would draw Upload in two colours on two pages, and a reader who learned the
 * colour once would be taught it means nothing. So it is said once, here, and
 * the lists are `as const` so a typo is a compile error rather than an
 * unstyled tag.
 *
 * It lives in `@wend/ui` rather than `@wend/shared` because it is a fact about
 * how things are drawn, not about the data: the API never needs to know that
 * Orders is mint. The rejected alternative was a per-app `identity.ts` in
 * `apps/web`; the admin app and the marketing site draw the same sources and
 * would have grown copies.
 *
 * ## WHY ENTRIES WITH NO CONSUMER YET
 *
 * Today only `upload` (a source) and `profile` (a field group) are drawn
 * anywhere. `pipeline`, `reach`, `derived`, `orders` and `consent` are
 * declared now, deliberately, because the next phases — pipeline sources,
 * Reach leads arriving as contacts, rule-based segments — each need one, and
 * the concept this is ported from shows what happens when each screen picks
 * its own: eleven lavenders, and three source colours borrowed from the status
 * palette. Declaring the slot before the screen exists is what stops the next
 * screen inventing a colour.
 *
 * ## THE HOLE IT LEAVES
 *
 * Nothing here enforces rule 1 of the identity block — that every identity
 * mark sits beside its word. `Tag`, `Badge` and the dots in `BySourceBars` and
 * `ScopeChips` all render the word next to the colour by construction; a
 * caller who draws a bare dot in `bg-ident-a` with no label is not stopped.
 *
 * `profile` maps to `neutral` and `derived` to `brand` on purpose: profile
 * fields are the plain default and a colour there would make every tag loud,
 * and "derived" means "Wend computed this", which is what violet already says.
 */

/** The four identity slots, in theme order. Decoration only; never status. */
export const IDENT_TONE_VALUES = ['ident-a', 'ident-b', 'ident-c', 'ident-d'] as const;
export type IdentTone = (typeof IDENT_TONE_VALUES)[number];

/** The groups a contact field belongs to, as the field picker groups them. */
export const FIELD_GROUP_VALUES = ['profile', 'derived', 'orders', 'reach', 'consent'] as const;
export type FieldGroup = (typeof FIELD_GROUP_VALUES)[number];

/** A tag tone that is not an identity hue: the plain default, or Wend's own violet. */
export type FieldGroupTone = 'neutral' | 'brand' | IdentTone;

/** Tag tone for each field group: neutral and brand are not identity hues. */
export const FIELD_GROUP_TONE: Readonly<Record<FieldGroup, FieldGroupTone>> = {
  profile: 'neutral',
  derived: 'brand',
  orders: 'ident-d',
  reach: 'ident-c',
  consent: 'ident-a',
};

/** Where a contact came from. */
export const SOURCE_KIND_VALUES = ['upload', 'pipeline', 'reach'] as const;
export type SourceKind = (typeof SOURCE_KIND_VALUES)[number];

/** The identity slot each source is drawn in. */
export const SOURCE_KIND_TONE = {
  upload: 'ident-a',
  pipeline: 'ident-b',
  reach: 'ident-c',
} as const satisfies Readonly<Record<SourceKind, IdentTone>>;
